/**
 * Measures (Units of Measure) Service
 * Manages units of measure catalog from the measures table
 */

import { supabase } from '@/lib/supabase'

export interface Measure {
  code: string
  symbol: string
  name: string
  category: string
  is_culinary: boolean
}

/**
 * List all active measures, optionally filtered by category
 * @param category - Optional category filter ('mass', 'volume', 'length', 'count', 'culinary')
 * @returns Array of measures sorted by category and name
 */
export async function listMeasures(category?: string): Promise<Measure[]> {
  const { data, error } = await supabase.rpc('list_units_by_category', {
    p_category: category || null
  })

  if (error) {
    console.error('Error fetching measures:', error)
    throw error
  }

  return data || []
}

/**
 * Get measures grouped by category for UI display
 * @returns Object with category keys and measure arrays as values
 */
export async function getMeasuresByCategory(): Promise<Record<string, Measure[]>> {
  const measures = await listMeasures()

  const grouped = measures.reduce((acc, measure) => {
    const category = measure.category || 'other'
    if (!acc[category]) {
      acc[category] = []
    }
    acc[category].push(measure)
    return acc
  }, {} as Record<string, Measure[]>)

  return grouped
}

/**
 * Get a single measure by code
 * @param code - Measure code (e.g., 'kg', 'L', 'col_sopa')
 * @returns The measure or null if not found
 */
export async function getMeasure(code: string): Promise<Measure | null> {
  const measures = await listMeasures()
  return measures.find((m) => m.code === code) || null
}
